import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";

import {
  LabelInput,
  FloatingLabelSelect,
} from "../../../../components/Reusable/Layout";
import { Button } from "../../../../components/Reusable/Button";
import { useAppDispatch, useAppSelector } from "../../../../hooks/hooks";
import {
  fetchDGCoinPlans,
  updateDGCoinPlan,
} from "../../../../features/auth/store/dgcoinPlanSlice";
import { fetchDGCoinProviders } from "../../../../features/auth/store/dgcoinProviderSlice";
import { fetchDGCoinCategories } from "../../../../features/auth/store/dgcoinCategorySlice";
import { fetchCountries } from "../../../../features/auth/store/countrySlice";
import { fetchCurrencies } from "../../../../features/auth/store/currencySlice";
import Modal from "../../../../components/Reusable/Modal";
import { DGCoinPlan } from "../../../../features/auth/store/types/dgcoin-types";
import {
  ReferralSettingsFields,
  referralEligibilityToFormValue,
} from "../ReferralSettingsFields";

type EditPlanProps = {
  isOpen: boolean;
  onClose?: () => void;
  dgCoinPlan?: DGCoinPlan;
};

export const EditDGCoinModal: React.FC<EditPlanProps> = ({
  isOpen,
  onClose,
  dgCoinPlan,
}) => {
  const [formData, setFormData] = useState({
    provider_id: "",
    sub_category_id: "",
    name: "",
    message: "",
    code: "",
    wallet_price: "",
    api_price: "",
    atm_price: "",
    status: "on",
    source: "",
    countries: [] as string[],
    discount: "",
    referral_eligible: "0",
    referral_amount: "",
    point_bonus: "",
    price_type: "fixed",
    price_currency_id: "",
    index: 1,
  });

  const [image, setImage] = useState<File | undefined>(undefined);
  const [currentImage, setcurrentImage] = useState<string | undefined>(
    undefined,
  );
  const [filteredSubCategories, setFilteredSubCategories] = useState<any[]>([]);
  const dispatch = useAppDispatch();
  const countries = useAppSelector((state) => state.countries?.items ?? []);
  const countriesLoading = useAppSelector(
    (state) => state.countries?.loading ?? false,
  );
  const { dgcoinProviders } = useAppSelector((state) => state.dgcoinProviders);
  const { dgcoinCategories } = useAppSelector(
    (state) => state.dgcoinCategories,
  );
  const { currentPage } = useAppSelector((state) => state.dgcoinPlans);
  const currencies = useAppSelector((state) => state.currencies?.items ?? []);

  const deleteOldImage =
    dgCoinPlan?.image_url !== null && currentImage === undefined;

  useEffect(() => {
    if (isOpen) {
      dispatch(fetchDGCoinProviders({ perPage: 200 }));
      dispatch(fetchCurrencies({ perPage: 200 }));
      dispatch(fetchCountries({ perPage: 200 }));
    }
  }, [dispatch, isOpen]);

  useEffect(() => {
    if (dgCoinPlan) {
      let countries = [] as string[];
      if (dgCoinPlan.countries) {
        const isString = dgCoinPlan.countries.every(
          (country) => typeof country === "string",
        );
        countries = isString
          ? (dgCoinPlan.countries as string[])
          : dgCoinPlan.countries.map((country) => String(country));
      }

      setcurrentImage(dgCoinPlan.image_url);
      setFormData({
        provider_id: dgCoinPlan.category?.provider_id?.toString() || "",
        sub_category_id: dgCoinPlan.category_id?.toString() || "",
        wallet_price: dgCoinPlan.wallet_price?.toString() || "",
        api_price: dgCoinPlan?.api_price?.toString() || "",
        atm_price: dgCoinPlan?.atm_price?.toString() || "",
        discount: dgCoinPlan.discount?.toString() || "",
        referral_eligible: referralEligibilityToFormValue(
          dgCoinPlan.referral_eligible,
        ),
        referral_amount: dgCoinPlan.referral_amount?.toString() || "",
        point_bonus: dgCoinPlan.point_bonus?.toString() || "",
        status: dgCoinPlan.status,
        source: dgCoinPlan.source,
        code: dgCoinPlan.code || "",
        price_type: dgCoinPlan.price_type || "fixed",
        price_currency_id: dgCoinPlan.price_currency_id?.toString() || "",
        name: dgCoinPlan.name || "",
        message: dgCoinPlan.message,
        index: dgCoinPlan.index || 1,
        countries,
      });
    }
  }, [dgCoinPlan]);

  useEffect(() => {
    if (formData.provider_id) {
      dispatch(fetchDGCoinCategories({ perPage: 200 }));
    }
  }, [dispatch, formData.provider_id]);

  useEffect(() => {
    if (formData.provider_id && dgcoinCategories.length) {
      setFilteredSubCategories(
        dgcoinCategories.filter(
          (cat) => String(cat.provider_id) === String(formData.provider_id),
        ),
      );
    } else {
      setFilteredSubCategories([]);
    }
  }, [formData.provider_id, dgcoinCategories]);

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "provider_id" ? { sub_category_id: "" } : {}),
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setImage(e.target.files[0]);
    }
  };

  const removeImage = () => {
    setImage(undefined);
  };

  const toggleCountry = (id: number) => {
    setFormData((prev) => {
      const exists = prev.countries.includes(String(id));
      return {
        ...prev,
        countries: exists
          ? prev.countries.filter((c) => c !== String(id))
          : [...prev.countries, String(id)],
      };
    });
  };

  const countryIds = countries.map((country) => String(country.id));
  const allCountriesSelected =
    countryIds.length > 0 &&
    countryIds.every((id) => formData.countries.includes(id));

  const toggleAllCountries = () => {
    setFormData((prev) => {
      const shouldDeselectAll = countryIds.every((id) =>
        prev.countries.includes(id),
      );
      return {
        ...prev,
        countries: shouldDeselectAll ? [] : countryIds,
      };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const formDataObj = new FormData();
    formDataObj.append("provider_id", formData.provider_id);
    formDataObj.append("sub_category_id", formData.sub_category_id);
    formDataObj.append("category_id", formData.sub_category_id);
    formDataObj.append("name", formData.name);
    formDataObj.append("message", formData.message || "");
    formDataObj.append("wallet_price", formData.wallet_price || "0");
    formDataObj.append("api_price", formData.api_price || "0");
    formDataObj.append("atm_price", formData.atm_price || "0");
    formDataObj.append("status", formData.status);
    formDataObj.append("source", formData.source);
    formDataObj.append("discount", formData.discount || "0");
    formDataObj.append("referral_eligible", formData.referral_eligible);
    formDataObj.append("referral_amount", formData.referral_amount || "0");
    formDataObj.append("point_bonus", formData.point_bonus);
    formDataObj.append("code", formData.code || "");
    formDataObj.append(
      "countries",
      JSON.stringify(formData.countries.map((c) => Number(c))),
    );
    formDataObj.append("price_type", formData.price_type || "fixed");
    formDataObj.append("price_currency_id", formData.price_currency_id);
    formDataObj.append("index", String(formData.index || 1));

    if (image) {
      formDataObj.append("image", image);
    }
    if (deleteOldImage) {
      formDataObj.append("deleteOldImage", deleteOldImage.toString());
    }
    const filters: any = { page: currentPage };
    try {
      await dispatch(
        updateDGCoinPlan({ data: formDataObj, id: dgCoinPlan?.id as number }),
      ).unwrap();
      toast.success("DG Coin plan updated successfully!");
      handleClose();
      await dispatch(fetchDGCoinPlans(filters));
    } catch (error: any) {
      toast.error(error?.message || "Failed to update DG Coin plan");
      console.error("Submission error:", error);
    }
  };

  const handleClose = () => {
    if (onClose) onClose();
    setFormData({
      provider_id: "",
      sub_category_id: "",
      name: "",
      message: "",
      wallet_price: "",
      api_price: "",
      atm_price: "",
      status: "on",
      source: "",
      countries: [],
      code: "",
      discount: "",
      referral_eligible: "0",
      referral_amount: "",
      point_bonus: "",
      price_type: "fixed",
      price_currency_id: "",
      index: 1,
    });
    setcurrentImage(undefined);
    setImage(undefined);
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      className="w-full max-w-md md:max-w-lg lg:max-w-xl mx-auto"
    >
      <div className="bg-white p-4 md:p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto dark:bg-black dark:text-white">
        <h2 className="text-lg md:text-xl font-bold mb-6">
          DG Coin Plan Details
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <FloatingLabelSelect
            name="provider_id"
            placeholder="Provider"
            value={formData.provider_id}
            onChange={handleInputChange}
            options={[
              { value: "", label: "Select Provider" },
              ...dgcoinProviders.map((p) => ({
                value: String(p.id),
                label: p.name,
              })),
            ]}
          />
          <FloatingLabelSelect
            name="sub_category_id"
            placeholder="Sub Category"
            value={formData.sub_category_id}
            onChange={handleInputChange}
            options={[
              { value: "", label: "Select Sub Category" },
              ...filteredSubCategories.map((cat) => ({
                value: cat.id,
                label: cat.name,
              })),
            ]}
            disabled={!formData.provider_id}
          />
          <FloatingLabelSelect
            name="status"
            value={formData.status}
            onChange={handleInputChange}
            placeholder="Status"
            options={[
              { value: "on", label: "Active" },
              { value: "off", label: "Inactive" },
            ]}
          />
          <LabelInput
            type="text"
            name="name"
            placeholder="Product Name"
            value={formData.name}
            onChange={handleInputChange}
            required
          />
          <FloatingLabelSelect
            name="price_currency_id"
            placeholder="Price Currency"
            value={formData.price_currency_id}
            onChange={handleInputChange}
            options={[
              { value: "", label: "Select Currency" },
              ...currencies.map((c) => ({
                value: String(c.id),
                label: `${c.name} (${c.symbol})`,
              })),
            ]}
            disabled={!currencies?.length}
          />
          <FloatingLabelSelect
            name="price_type"
            placeholder="Price Type"
            value={formData.price_type}
            onChange={handleInputChange}
            options={[
              { value: "fixed", label: "Fixed" },
              { value: "percent", label: "Percent" },
            ]}
          />
          <LabelInput
            type="number"
            name="wallet_price"
            placeholder="Wallet Price"
            value={formData.wallet_price}
            onChange={handleInputChange}
            required
          />
          <LabelInput
            type="number"
            name="api_price"
            placeholder="API Price"
            value={formData.api_price}
            onChange={handleInputChange}
            required
          />
          <LabelInput
            type="number"
            name="atm_price"
            placeholder="ATM Price"
            value={formData.atm_price}
            onChange={handleInputChange}
            required
          />
          <LabelInput
            type="number"
            name="point_bonus"
            placeholder="Point Bonus"
            value={formData.point_bonus}
            onChange={handleInputChange}
          />
          <LabelInput
            type="number"
            name="discount"
            placeholder="Discount Offered"
            value={formData.discount}
            onChange={handleInputChange}
            required
          />
          <ReferralSettingsFields
            referralEligible={formData.referral_eligible}
            referralAmount={formData.referral_amount}
            priceType={formData.price_type}
            onChange={handleInputChange}
          />
          <LabelInput
            type="text"
            name="source"
            placeholder="Source"
            value={formData.source}
            onChange={handleInputChange}
            required
          />
          <LabelInput
            type="text"
            name="code"
            placeholder="Code (Optional)"
            value={formData.code}
            onChange={handleInputChange}
          />
          <LabelInput
            type="number"
            name="index"
            placeholder="Index"
            value={String(formData.index)}
            onChange={handleInputChange}
          />
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-2">
            Countries (availability)
          </h3>
          <div className="border rounded-md p-3 max-h-48 overflow-y-auto">
            {countriesLoading ? (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
              </div>
            ) : countries.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-white">
                No countries found. Create countries first.
              </p>
            ) : (
              <div className="grid grid-cols-1 gap-2">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-white border-b pb-2 mb-1">
                  <input
                    type="checkbox"
                    checked={allCountriesSelected}
                    onChange={toggleAllCountries}
                  />
                  <span>Select all countries</span>
                </label>
                {countries.map((c) => (
                  <label
                    key={c.id}
                    className="flex items-center gap-2 text-sm text-gray-700 dark:text-white"
                  >
                    <input
                      type="checkbox"
                      checked={formData.countries.includes(String(c.id))}
                      onChange={() => toggleCountry(c.id)}
                    />
                    <span>
                      {c.name} ({c.symbol})
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>
          <div className="space-y-4">
            <p className="text-base font-semibold">Product Image</p>
            <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center dark:bg-white/10">
              <input
                type="file"
                id="fileUpload"
                onChange={handleFileChange}
                className="hidden"
                accept="image/*"
              />
              <label
                htmlFor="fileUpload"
                className="flex flex-col items-center justify-center cursor-pointer"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="mb-2"
                >
                  <path
                    d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15"
                    stroke="#718096"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M17 8L12 3L7 8"
                    stroke="#718096"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M12 3V15"
                    stroke="#718096"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  Upload from computer
                </span>
              </label>
            </div>
            {image && (
              <div className="mt-4 grid grid-cols-3 gap-4">
                <div className="relative rounded-md overflow-hidden h-20">
                  <img
                    src={URL.createObjectURL(image)}
                    alt="product"
                    className="w-full h-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => removeImage()}
                    className="absolute top-1 right-1 bg-white rounded-full p-1"
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M18 6L6 18"
                        stroke="#E53E3E"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M6 6L18 18"
                        stroke="#E53E3E"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            )}
            {currentImage && (
              <div>
                <p className="text-base font-semibold">Current Product Image</p>
                <div className="mt-4 grid grid-cols-3 gap-4">
                  <div className="relative rounded-md overflow-hidden h-20">
                    <img
                      src={currentImage}
                      alt="product"
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => setcurrentImage(undefined)}
                      className="absolute top-1 right-1 bg-white rounded-full p-1"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M18 6L6 18"
                          stroke="#E53E3E"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M6 6L18 18"
                          stroke="#E53E3E"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div>
            <p className="text-sm font-medium mb-1">Message</p>
            <textarea
              name="message"
              placeholder="Message"
              value={formData.message}
              onChange={handleInputChange}
              className="w-full h-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-white/10 dark:border-[#333333] dark:text-white"
            />
          </div>
          <div className="flex flex-col-reverse sm:flex-row gap-3 mt-6">
            <Button
              type="button"
              onClick={handleClose}
              bgColor="bg-gray-300 hover:bg-gray-400"
              textColor="text-gray-700"
              borderRadius="rounded-lg"
              className="px-6 py-2.5 sm:px-8 sm:py-2 w-full sm:w-[150px]"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              bgColor="bg-gradient-to-r from-[#027EC2] to-[#039EF2]"
              textColor="text-white"
              borderRadius="rounded-lg"
              className="px-6 py-2.5 sm:px-8 sm:py-2 w-full sm:w-[150px]"
            >
              Submit
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};
